import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    TextField, Grid, CircularProgress,
    Snackbar, Alert,
} from '@mui/material';
import { ArrowBack, Save as SaveIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { accountService } from '../../../shared/services/accountService';
import { AccountFormData } from '../../../shared/types/account';

const EMPTY_FORM: AccountFormData = {
    title: '',
    description: '',
};

export const AddEditAccount: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const isAdd = !id;

    const [form, setForm] = useState<AccountFormData>(EMPTY_FORM);
    const [errors, setErrors] = useState<Partial<Record<keyof AccountFormData, string>>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAccount, setIsLoadingAccount] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });

    useEffect(() => {
        if (id) loadAccount(id);
    }, [id]);

    const loadAccount = async (accountId: string) => {
        setIsLoadingAccount(true);
        try {
            const res = await accountService.getById(accountId);
            const account = res.data?.account || res.data?.result || (res as any)?.data;
            if (account) {
                setForm({
                    title: account.title || '',
                    description: account.description || '',
                });
            }
        } catch (err: any) {
            setAlert({
                show: true, type: 'error',
                message: err.response?.data?.message || 'Error loading account details',
            });
        } finally {
            setIsLoadingAccount(false);
        }
    };

    const validate = (): boolean => {
        const newErrors: Partial<Record<keyof AccountFormData, string>> = {};
        if (!form.title.trim()) newErrors.title = 'Title is required';
        else if (form.title.trim().length < 2) newErrors.title = 'Title must be at least 2 characters';
        else if (form.title.trim().length > 200) newErrors.title = 'Title cannot exceed 200 characters';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const set = (key: keyof AccountFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(f => ({ ...f, [key]: e.target.value }));
        if (errors[key]) setErrors(err => ({ ...err, [key]: '' }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setIsLoading(true);
        try {
            if (isAdd) {
                await accountService.create(form);
            } else {
                await accountService.update(id!, form);
            }
            setAlert({
                show: true, type: 'success',
                message: isAdd ? 'Account created successfully' : 'Account updated successfully',
            });
            setTimeout(() => navigate('/accounts'), 1000);
        } catch (err: any) {
            setAlert({
                show: true, type: 'error',
                message: err.response?.data?.message || err.message || 'An error occurred',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: '#fff',
            border: '1px solid #e2e8f0',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'transparent' },
            '&.Mui-focused fieldset': { borderColor: 'transparent', border: '1px solid #6366f1' },
        },
        '& .MuiInputBase-input': { color: '#1f2937', fontSize: '15px' },
    };

    return (
        <div className="add-edit-admin-container p-[40px]">
            <div className="add-edit-admin-content">
                <div className="form-wrapper max-w-3xl">

                    {/* Back */}
                    <div className="-ml-4 mb-2">
                        <Link to="/accounts" className="back-link">
                            <ArrowBack className="text-[#6366f1]" />
                            <span className="ml-2 text-[#6366f1]">Back to Accounts</span>
                        </Link>
                    </div>

                    <div className="title text-2xl sm:text-5xl">
                        {isAdd ? 'Add Account' : 'Edit Account'}
                    </div>

                    <div className="form-card mt-8">
                        {isLoadingAccount ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <CircularProgress size={50} sx={{ color: '#6366f1' }} />
                                <p className="mt-4 text-gray-600 font-medium">Loading account details...</p>
                            </div>
                        ) : (
                            <>
                                {alert.show && (
                                    <Alert
                                        severity={alert.type}
                                        className="mb-8 rounded-xl"
                                        onClose={() => setAlert(a => ({ ...a, show: false }))}
                                    >
                                        {alert.message}
                                    </Alert>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                Title <span className="text-red-500">*</span>
                                            </label>
                                            <TextField
                                                fullWidth required
                                                value={form.title}
                                                onChange={set('title')}
                                                error={!!errors.title}
                                                helperText={errors.title}
                                                placeholder="Enter account title"
                                                inputProps={{ maxLength: 200 }}
                                                sx={fieldStyles}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                Description
                                            </label>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={form.description}
                                                onChange={set('description')}
                                                placeholder="Enter account description (optional)"
                                                sx={{
                                                    ...fieldStyles,
                                                    '& .MuiOutlinedInput-root': {
                                                        ...fieldStyles['& .MuiOutlinedInput-root'],
                                                        height: 'auto',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Actions */}
                                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
                                        <AppButton
                                            type="button"
                                            variant="cancel"
                                            onClick={() => navigate('/accounts')}
                                            sx={{ px: 4 }}
                                        >
                                            Cancel
                                        </AppButton>
                                        <AppButton
                                            type="submit"
                                            variant="primary"
                                            startIcon={<SaveIcon />}
                                            loading={isLoading}
                                            sx={{ px: 5 }}
                                        >
                                            {isAdd ? 'Create Account' : 'Update Account'}
                                        </AppButton>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Snackbar
                open={alert.show}
                autoHideDuration={4000}
                onClose={() => setAlert(a => ({ ...a, show: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={alert.type} variant="filled" className="rounded-xl">
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};
